import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";
import { Component, ReactNode } from "react";
import { forceAppUpdate, isStaleChunkErrorMessage } from "@/lib/forceAppUpdate";
import { isTypingInInput, showUpdateBanner } from "@/hooks/useUpdateBanner";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function isStaleChunkError(error: Error | null): boolean {
  return isStaleChunkErrorMessage(error?.message);
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    // One automatic hard update for stale chunks after deploy -- but
    // never while someone's actively typing (see main.tsx's matching
    // vite:preloadError check for the full explanation of why this
    // check exists). By the time a render-time throw reaches this
    // boundary the crashed subtree is already gone regardless, but
    // skipping the extra forced reload here still avoids compounding
    // that with a full page navigation on top of it.
    if (!isStaleChunkError(error)) return;
    if (sessionStorage.getItem("chunk-reload-attempted")) return;
    if (isTypingInInput()) {
      showUpdateBanner();
      return;
    }
    sessionStorage.setItem("chunk-reload-attempted", "true");
    forceAppUpdate();
  }

  render() {
    if (this.state.hasError) {
      const stale = isStaleChunkError(this.state.error);
      const willAutoReload =
        stale && !sessionStorage.getItem("chunk-reload-attempted");

      if (willAutoReload) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8 bg-background">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <RotateCcw size={32} className="animate-spin" />
              <p className="text-sm">Updating to the latest version...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div
            className={cn(
              "w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-lg"
            )}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">
              {stale ? "Update required" : "An unexpected error occurred."}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {stale
                ? "This tab is running an older version of FreeDailyPro (common right after we deploy). Load the latest version to continue."
                : "Something went wrong. Reloading the page usually fixes it."}
            </p>
            {this.state.error && !stale && (
              <pre className="mb-6 max-h-32 overflow-auto rounded-lg bg-muted p-3 text-left text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
            {stale && this.state.error && (
              <pre className="mb-6 max-h-24 overflow-auto rounded-lg bg-muted p-3 text-left text-xs text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
            <button
              type="button"
              onClick={() => forceAppUpdate()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              <RefreshCw className="h-4 w-4" />
              {stale ? "Update now" : "Reload page"}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
